import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, type ArticleSection } from '@/mocks/articles';

function renderArticleContent(sections: ArticleSection[]) {
  return sections.map((section, idx) => {
    switch (section.type) {
      case 'heading':
        return (
          <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4 mt-10 first:mt-0">
            {section.heading}
          </h2>
        );
      case 'subheading':
        return (
          <h3 key={idx} className="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3">
            {section.subheading}
          </h3>
        );
      case 'paragraphs':
        return (
          <div key={idx} className="space-y-3">
            {section.paragraphs?.map((p, pIdx) => (
              <p key={pIdx}>{p}</p>
            ))}
          </div>
        );
      case 'image':
        return (
          <div key={idx} className="relative rounded-2xl overflow-hidden my-8">
            <img
              src={section.imageUrl}
              alt={section.imageAlt || 'Sugargoo Spreadsheet article image'}
              className="w-full aspect-[2.4/1] object-cover rounded-2xl"
              title={section.imageAlt || ''}
            />
          </div>
        );
      case 'tip':
        return (
          <div key={idx} className="p-5 md:p-6 rounded-xl bg-accent-50 border border-accent-200/60 my-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500 text-white shrink-0">
                <i className="ri-lightbulb-line text-sm"></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">Expert Tip</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{section.tipText}</p>
              </div>
            </div>
          </div>
        );
      case 'faq':
        return (
          <div key={idx} className="space-y-4 mt-6">
            {section.faqs?.map((faq, fIdx) => (
              <div key={fIdx} className="p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/60">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-foreground-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  });
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between h-14 md:h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
                  <i className="ri-database-2-line text-base md:text-lg text-white"></i>
                </div>
                <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base whitespace-nowrap">
                  Sugargoo <span className="text-primary-600">Spreadsheet</span>
                </span>
              </Link>
              <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors font-medium whitespace-nowrap">
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>
            </div>
          </div>
        </nav>
        <div className="pt-20 pb-20 text-center px-4">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.thumbnailImage,
    datePublished: '2026-08-01',
    dateModified: '2026-08-06',
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet',
      url: 'https://sugargoosheet.hair',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://sugargoosheet.hair/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-database-2-line text-base md:text-lg text-white"></i>
              </div>
              <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base whitespace-nowrap">
                Sugargoo <span className="text-primary-600">Spreadsheet</span>
              </span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors font-medium whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <article className="pt-16 md:pt-20">
        <header className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 transition-colors font-medium">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-xs text-foreground-500">{article.title}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              {article.description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <span className="text-xs text-foreground-500">Updated: August 2026</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6 text-foreground-700 text-sm md:text-base leading-relaxed">
              {renderArticleContent(article.sections)}
            </div>

            <div className="pt-6 border-t border-background-200 mt-10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-600 font-medium">Was this guide helpful?</p>
                  <p className="text-xs text-foreground-400 mt-1">Explore more articles and guides on the homepage.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Back to Home <i className="ri-home-4-line"></i>
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
                  >
                    Explore Spreadsheet <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <footer className="w-full bg-background-950 text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-database-2-line text-sm text-white"></i>
              </div>
              <span className="font-heading font-semibold text-white text-sm whitespace-nowrap">
                Sugargoo <span className="text-primary-400">Spreadsheet</span>
              </span>
            </Link>
            <p className="text-xs text-foreground-400">
              &copy; 2026 Sugargoo Spreadsheet. Independent informational resource.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}